import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { telegramUsersApi } from 'api';
import {
  CloseFormikDialogResult,
  ConfirmButton,
  CrudPage,
  CrudTableActionType,
  CrudTableAction,
  DataGridColumnDefinition,
  dataGridColumns,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { useMutation, usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, TelegramUser } from 'types';
import { filtersUtils, validationUtils } from 'utils';

import { TelegramUserDetailsDialog } from './TelegramUserDetailsDialog';
import { TelegramUserSignInDialog } from './TelegramUserSignInDialog';

type TelegramUserFilters = {
  id?: string;
  name?: string;
  phone?: string;
  isAuthenticated?: string;
};

export const UsersTab: React.FC = () => {
  const queryClient = useQueryClient();

  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.telegram_flow.telegram_users',
  });

  const [signInDialogProps, setSignInDialogProps] = useState<{
    open: boolean;
    telegramUserId?: string;
  }>({ open: false });

  const [editDialogProps, setEditDialogProps] = useState<{
    open: boolean;
    data?: TelegramUser;
  }>({ open: false });

  const queryKey = useMemo(() => QueryKey.TelegramUsers, []);

  const queryResult = usePartialQuery(
    queryKey,
    telegramUsersApi.getAllPaginated,
  );

  const handleUsersChange = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.TelegramUsers);
  }, [queryClient]);

  const updateMutation = useMutation(telegramUsersApi.update, {
    onSuccess: handleUsersChange,
    notifierType: 'save',
  });

  const { mutate: remove } = useMutation(telegramUsersApi.remove, {
    onSuccess: handleUsersChange,
    notifierType: 'remove',
  });

  const { mutate: login } = useMutation(telegramUsersApi.login, {
    onSuccess: () => queryResult.refetch(),
    notifierType: 'execute',
  });

  const { mutate: logout } = useMutation(telegramUsersApi.logout, {
    onSuccess: () => queryResult.refetch(),
    notifierType: 'execute',
  });

  const signInMutation = useMutation(telegramUsersApi.signIn, {
    onSuccess: () => {
      queryResult.refetch();
      setSignInDialogProps({ open: false });
    },
    notifierType: 'execute',
  });

  const columns: DataGridColumnDefinition<TelegramUser>[] = useMemo(
    () => [
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.name'),
        valueKey: 'name',
      },
      {
        header: t('fields.phone'),
        valueGetter: (item) => (
          <div className="tw-flex tw-items-center">
            {item.phone}
            {item.isAuthenticated && (
              <Tooltip title={t('tooltip.authenticated')}>
                <CheckCircleOutlineIcon
                  sx={{ ml: 1 }}
                  fontSize="small"
                  color="success"
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [t],
  );

  const filtersDefinitions: FilterDefinition<TelegramUserFilters>[] = useMemo(
    () => [
      {
        label: t('filters.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('filters.name'),
        name: 'name',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.phone'),
        name: 'phone',
        type: FilterDefinitionType.Text,
      },
      filtersUtils.getBooleanStatusFilter<TelegramUserFilters>(
        t('filters.authenticated'),
        'isAuthenticated',
      ),
    ],
    [t],
  );

  const handleSignInDialogClose = useCallback(
    (result: CloseFormikDialogResult<{ code: string }>) => {
      if (result.ok && result.data && signInDialogProps.telegramUserId) {
        signInMutation.mutate({
          id: signInDialogProps.telegramUserId,
          data: { code: result.data.values.code },
        });
      } else {
        setSignInDialogProps({ open: false });
      }
    },
    [signInMutation, signInDialogProps.telegramUserId],
  );

  const handleEditDialogClose = useCallback(
    (result: CloseFormikDialogResult<{ name: string; phone: string }>) => {
      if (result.ok && result.data && editDialogProps.data) {
        updateMutation.mutate(
          {
            id: editDialogProps.data.id,
            data: result.data.values,
          },
          {
            onSuccess: () => setEditDialogProps({ open: false }),
            onError: (error: AxiosError) => {
              result.data?.formikHelpers.setErrors(
                validationUtils.getFormErrors(error),
              );
            },
          },
        );
      } else {
        setEditDialogProps({ open: false });
      }
    },
    [editDialogProps.data, updateMutation],
  );

  const actions: CrudTableAction<TelegramUser>[] = useMemo(
    () => [
      {
        type: CrudTableActionType.Custom,
        render: (item: TelegramUser) =>
          !item.isAuthenticated ? (
            <ConfirmButton onConfirm={() => login(item.id)}>
              {({ onClick }) => (
                <Tooltip title={t('actions.login')}>
                  <IconButton size="small" color="success" onClick={onClick}>
                    <LoginIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmButton>
          ) : null,
      },
      {
        type: CrudTableActionType.Custom,
        render: (item: TelegramUser) =>
          item.isAuthenticated ? (
            <ConfirmButton onConfirm={() => logout(item.id)}>
              {({ onClick }) => (
                <Tooltip title={t('actions.logout')}>
                  <IconButton size="small" onClick={onClick}>
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              )}
            </ConfirmButton>
          ) : null,
      },
      {
        type: CrudTableActionType.Custom,
        render: (item: TelegramUser) =>
          !item.isAuthenticated && !!item.phoneCodeHash ? (
            <Tooltip title={t('actions.sign_in')}>
              <IconButton
                size="small"
                onClick={() =>
                  setSignInDialogProps({ open: true, telegramUserId: item.id })
                }
              >
                <KeyIcon />
              </IconButton>
            </Tooltip>
          ) : null,
      },
      {
        type: CrudTableActionType.Details,
        onClick: (item: TelegramUser) => {
          setEditDialogProps({ open: true, data: item });
        },
      },
      {
        type: CrudTableActionType.Remove,
        onRemove: (item: TelegramUser, { close }: { close: () => void }) =>
          remove(item.id, { onSuccess: close }),
      },
    ],
    [t, login, logout, remove],
  );

  return (
    <Fragment>
      <CrudPage
        filters={{ filtersDefinitions }}
        table={{
          queryResult,
          columns,
          paginated: true,
          actions,
          ignoreState: { refetchLoading: true },
        }}
      />
      <TelegramUserSignInDialog
        open={signInDialogProps.open}
        telegramUserId={signInDialogProps.telegramUserId}
        mutation={signInMutation}
        onClose={handleSignInDialogClose}
      />
      <TelegramUserDetailsDialog
        open={editDialogProps.open}
        data={editDialogProps.data}
        mutation={updateMutation}
        onClose={handleEditDialogClose}
      />
    </Fragment>
  );
};
